import { notify } from "@general-backoffice/core";
import type { User } from "@investhinks/api-client";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Badge, Button, Card, CardBody, CardHeader, Col, Row } from "reactstrap";

import { type BillingData, invoicingEndpoints, type StrapiId, type TaxProfile, usersEndpoints } from "../../../api";
import { isPartnerType } from "../../../constants/partnerTypes";
import { clearCache } from "../../../hooks/useFetch";
import { login, refreshImpersonation } from "../../../redux/reducers/session/actions";
import type { RootState } from "../../../redux/reducers/session/state";
import getErrorMessage from "../../../utils/getErrorMessage";
import OnboardingWizard from "./OnboardingWizard";
import TaxReferrerTypeBadge from "./TaxReferrerTypeBadge";
import ThresholdStatus, { ThresholdStatusIcon } from "./ThresholdStatus";
import VIESVerification from "./VIESVerification";

// User with optional taxProfile for this card
// Accepts both the full User type from api-client and simplified versions
interface TaxProfileUser {
  id: StrapiId;
  taxProfile?: TaxProfile | null;
  referrerType?: string | null;
  [key: string]: unknown;
}

interface TaxProfileCardProps {
  user: TaxProfileUser | null | undefined;
  onUpdate?: () => void | Promise<void>;
  isLoading?: boolean;
  billingData?: BillingData | null | undefined;
  autoOpenWizard?: boolean;
  onWizardAutoOpened?: (() => void) | null;
  readOnly?: boolean;
}

/**
 * TaxProfileCard - Displays a summary of the user's tax profile.
 * Shows referrer type, tax ID, thresholds, and VIES status.
 * Includes edit button to open the full tax profile modal.
 *
 * Automatically determines whether to use /users/me or /users/:id
 * based on whether the current session user is an admin.
 */
const TaxProfileCard: React.FC<TaxProfileCardProps> = ({
  user,
  onUpdate,
  isLoading = false,
  billingData = null,
  autoOpenWizard = false,
  onWizardAutoOpened = null,
  readOnly = false,
}) => {
  const dispatch = useDispatch();
  const { user: sessionUser, token, impersonatedUserId } = useSelector((state: RootState) => state.sessionState);
  const isAdmin = sessionUser?.role?.name === "Admin";
  const { t } = useTranslation("billingData", { keyPrefix: "taxProfile" });
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [wizardTaxProfile, setWizardTaxProfile] = useState<TaxProfile | null>({});
  const [isWizardLoading, setIsWizardLoading] = useState(false);

  const taxProfile = user?.taxProfile;
  const hasProfile = !!taxProfile?.profileType;

  // Use server-calculated completeness (added by middleware)
  const profileComplete = !!taxProfile?.taxProfileComplete;

  // Sync wizard state with user's tax profile
  // If no taxProfile exists but billingData does, prefill from billing data
  useEffect(() => {
    const existingProfile = user?.taxProfile || {};
    const hasCompleteProfile = !!existingProfile.profileType;

    // If profile is already set up, just use it
    if (hasCompleteProfile) {
      setWizardTaxProfile(existingProfile);
      return;
    }

    // Build prefilled data from old billing data
    const prefilled = { ...existingProfile };

    if (billingData) {
      // Name - only prefill if not already set
      if (billingData.name && !prefilled.name) {
        prefilled.name = billingData.name;
      }

      // Tax ID number - only prefill if not already set
      if (billingData.tax_id && !prefilled.taxIdNumber) {
        prefilled.taxIdNumber = billingData.tax_id;
      }

      // Build fiscal address from address components - only if not already set
      if (!prefilled.fiscalAddress) {
        const addressParts = [
          billingData.line1,
          billingData.line2,
          [billingData.postal_code, billingData.city].filter(Boolean).join(" "),
          billingData.state,
        ].filter(Boolean);
        if (addressParts.length > 0) {
          prefilled.fiscalAddress = addressParts.join(", ");
        }
      }

      // Country - only prefill if not already set
      if (billingData.country && !prefilled.country) {
        prefilled.country = billingData.country;
      }
    }

    setWizardTaxProfile(prefilled);
  }, [user?.taxProfile, billingData]);

  // Auto-open wizard when requested (e.g., from URL param or migration banner)
  useEffect(() => {
    if (autoOpenWizard && user && !isLoading) {
      setIsWizardOpen(true);
      onWizardAutoOpened?.();
    }
  }, [autoOpenWizard, user, isLoading, onWizardAutoOpened]);

  // Handle VIES verification from card
  const handleVerifyVIES = async () => {
    if (!taxProfile?.taxIdNumber || !user) {
      notify.error(t("vies.noVatNumber"));
      return;
    }

    try {
      const { data: result } = await invoicingEndpoints.verifyVIES({
        vatNumber: taxProfile.taxIdNumber,
        userId: user.id,
      });
      if (result.valid) {
        notify.success(t("vies.verificationSuccess"));
        onUpdate?.();
      } else {
        notify.warning(result.error || t("vies.verificationFailed"));
      }
    } catch (err: unknown) {
      notify.error(getErrorMessage(err, t("vies.verificationError")));
    }
  };

  // Handle VIES verification from wizard
  const handleWizardVerifyVIES = async (vatNumber: string) => {
    if (!user) return;

    try {
      const { data: result } = await invoicingEndpoints.verifyVIES({ vatNumber, userId: user.id });

      if (result.valid) {
        setWizardTaxProfile((prev) => ({
          ...prev,
          viesVerified: true,
          viesVerifiedAt: result.verifiedAt || new Date().toISOString(),
          viesName: result.name,
          viesAddress: result.address,
          name: result.name || prev?.name,
          fiscalAddress: result.address || prev?.fiscalAddress,
        }));
        notify.success(t("vies.verificationSuccess"));
      } else {
        notify.warning(result.error || t("vies.verificationFailed"));
      }
    } catch (err: unknown) {
      notify.error(getErrorMessage(err, t("vies.verificationError")));
    }
  };

  // Handle wizard completion
  // Admins can update any user via /users/:id, non-admins use /users/me
  // Accepts finalProfile to avoid race condition with React state updates
  const handleWizardComplete = async (finalProfile?: TaxProfile) => {
    if (!user) return;

    const profileToSave = finalProfile || wizardTaxProfile;
    setIsWizardLoading(true);
    try {
      if (isAdmin) {
        await usersEndpoints.updateUser(user.id, { taxProfile: { ...profileToSave } });
      } else {
        await usersEndpoints.updateMe({ taxProfile: { ...profileToSave } });

        // Update only the taxProfile in Redux session (preserve other fields like role)
        // This triggers the migration status hook to refetch
        if (!sessionUser) return;
        const updatedUser: User = {
          ...sessionUser,
          taxProfile: { ...sessionUser.taxProfile, ...profileToSave, taxProfileComplete: true },
        };
        if (impersonatedUserId) {
          dispatch(refreshImpersonation(updatedUser));
        } else {
          dispatch(login(token || "", updatedUser));
        }

        // Clear migration status cache so banner disappears for current user
        clearCache("tax_profile_migration_status");
      }
      setIsWizardOpen(false);
      notify.success(t("form.saveSuccess"));
      // Wait for onUpdate to complete before finishing
      // This ensures the parent fetches and displays the updated data
      if (onUpdate) {
        await onUpdate();
      }
    } catch (err: unknown) {
      notify.error(getErrorMessage(err, t("form.saveError")));
    } finally {
      setIsWizardLoading(false);
    }
  };

  // Determine if VIES section should be shown
  const showVIES = taxProfile?.profileType === "EU_BUSINESS";

  return (
    <>
      <Card className="shadow">
        <CardHeader className="bg-transparent border-0">
          <Row className="align-items-center">
            <Col>
              <h3 className="mb-0">
                {t("title")}
                {taxProfile?.thresholdBlocked && (
                  <ThresholdStatusIcon
                    thresholdBlocked={true}
                    thresholdWarning={false}
                    thresholdBlockReason={taxProfile?.thresholdBlockReason ?? undefined}
                    id={`threshold-icon-blocked-${user?.id || "card"}`}
                  />
                )}
                {taxProfile?.thresholdWarning && !taxProfile?.thresholdBlocked && (
                  <ThresholdStatusIcon
                    thresholdWarning={true}
                    thresholdBlocked={false}
                    thresholdBlockReason={undefined}
                    id={`threshold-icon-warning-${user?.id || "card"}`}
                  />
                )}
              </h3>
            </Col>
            <Col className="text-right">
              {!readOnly && (
                <Button color="primary" size="sm" onClick={() => setIsWizardOpen(true)} disabled={isLoading}>
                  {hasProfile ? t("form.edit") : t("form.setup")}
                </Button>
              )}
            </Col>
          </Row>
        </CardHeader>

        <CardBody>
          {!hasProfile ? (
            <div className="text-center text-muted py-4">
              <i className="fas fa-file-invoice fa-3x mb-3" />
              <p>{t("noProfile")}</p>
              {!readOnly && (
                <Button color="primary" size="sm" onClick={() => setIsWizardOpen(true)}>
                  {t("form.setup")}
                </Button>
              )}
            </div>
          ) : (
            <>
              {/* Referrer Type Badge */}
              <div className="mb-3">
                <small className="text-muted d-block mb-1">{t("form.profileType")}</small>
                <TaxReferrerTypeBadge type={taxProfile.profileType ?? ""} id={`tax-badge-${user?.id || "card"}`} />
              </div>

              {/* Tax ID */}
              {taxProfile.taxIdNumber && (
                <div className="mb-3">
                  <small className="text-muted d-block mb-1">{t("form.taxIdNumber")}</small>
                  <span className="font-weight-bold">
                    {taxProfile.taxIdType}: {taxProfile.taxIdNumber}
                  </span>
                  {taxProfile.taxIdVerified && (
                    <Badge color="success" className="ml-2" pill>
                      <i className="fas fa-check mr-1" />
                      {t("verified")}
                    </Badge>
                  )}
                </div>
              )}

              {/* Company Name */}
              {taxProfile.name && (
                <div className="mb-3">
                  <small className="text-muted d-block mb-1">{t("form.name")}</small>
                  <span>{taxProfile.name}</span>
                </div>
              )}

              {/* Activity Type (for Spanish autónomos only) */}
              {taxProfile.activityType &&
                (taxProfile.profileType === "SPANISH_AUTONOMO_EMPRESARIAL" ||
                  taxProfile.profileType === "SPANISH_AUTONOMO_PROFESIONAL") && (
                  <div className="mb-3">
                    <small className="text-muted d-block mb-1">{t("form.activityType")}</small>
                    <span>{t(`activityTypes.${taxProfile.activityType}`)}</span>
                  </div>
                )}

              {/* IRPF Rate (for profesional only) */}
              {taxProfile.irpfRate != null &&
                taxProfile.irpfRate > 0 &&
                taxProfile.profileType === "SPANISH_AUTONOMO_PROFESIONAL" && (
                  <div className="mb-3">
                    <small className="text-muted d-block mb-1">{t("form.irpfRate")}</small>
                    <span>{(taxProfile.irpfRate * 100).toFixed(0)}%</span>
                  </div>
                )}

              {/* Threshold Status */}
              <ThresholdStatus
                thresholdWarning={taxProfile?.thresholdWarning ?? false}
                thresholdBlocked={taxProfile?.thresholdBlocked ?? false}
                thresholdBlockReason={taxProfile?.thresholdBlockReason ?? undefined}
                id={`threshold-status-${user?.id || "card"}`}
              />

              {/* VIES Verification for EU Business */}
              {showVIES && (
                <div className="mt-3">
                  {/* TODO: Fix type - VIESVerification doesn't accept className prop */}
                  <VIESVerification taxProfile={taxProfile} onVerify={handleVerifyVIES} isLoading={isWizardLoading} />
                </div>
              )}

              {/* Profile Completion Status */}
              <div className="mt-3 pt-3 border-top">
                <small className="text-muted">
                  {profileComplete ? (
                    <span className="text-success">
                      <i className="fas fa-check-circle mr-1" />
                      {t("profileComplete")}
                    </span>
                  ) : (
                    <span className="text-warning">
                      <i className="fas fa-exclamation-triangle mr-1" />
                      {t("profileIncomplete")}
                    </span>
                  )}
                </small>
              </div>
            </>
          )}
        </CardBody>
      </Card>

      {/* Onboarding Wizard */}
      <OnboardingWizard
        isOpen={isWizardOpen}
        onClose={() => setIsWizardOpen(false)}
        taxProfile={wizardTaxProfile}
        setTaxProfile={setWizardTaxProfile}
        onComplete={handleWizardComplete}
        onVerifyVIES={handleWizardVerifyVIES}
        isLoading={isWizardLoading}
        isPartner={isPartnerType(user?.referrerType ?? "")}
      />
    </>
  );
};

export default TaxProfileCard;
